import React, { useState, useEffect, useCallback } from 'react';
import { productsService } from '../../api/apiClient';
import { useToast } from '../../context/ToastContext';
import { Card, CardHeader, CardBody } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input, Textarea } from '../../components/UI/Input';
import { Badge } from '../../components/UI/Badge';
import { Modal } from '../../components/UI/Modal';
import { ConfirmDialog } from '../../components/UI/ConfirmDialog';
import { Spinner } from '../../components/UI/Spinner';
import { EmptyState } from '../../components/UI/EmptyState';
import {
  Boxes,
  Plus,
  Search,
  History,
  Edit2,
  Trash2,
  TrendingUp,
  TrendingDown,
  ArrowUpDown,
  Filter,
} from 'lucide-react';

export const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterLowStock, setFilterLowStock] = useState(false);

  // Modals
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [kardexModalOpen, setKardexModalOpen] = useState(false);
  const [movements, setMovements] = useState([]);
  const [kardexLoading, setKardexLoading] = useState(false);

  // Delete
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Product Form State
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    description: '',
    price: '',
    cost: '',
    stock: '0',
    min_stock: '5',
  });
  const [errors, setErrors] = useState({});

  // Stock Adjustment Form State
  const [stockForm, setStockForm] = useState({
    quantity: '',
    type: 'add', // 'add', 'subtract', 'set'
    notes: '',
  });

  const { success, error: toastError } = useToast();

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await productsService.list({
        search,
        low_stock: filterLowStock ? 1 : 0,
      });
      if (res && res.data) {
        setProducts(Array.isArray(res.data) ? res.data : (res.data.data || []));
      }
    } catch (err) {
      toastError(err.message || 'Error al cargar productos.');
    } finally {
      setLoading(false);
    }
  }, [search, filterLowStock, toastError]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProducts();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchProducts]);

  const openCreateModal = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      sku: '',
      description: '',
      price: '',
      cost: '',
      stock: '0',
      min_stock: '5',
    });
    setErrors({});
    setProductModalOpen(true);
  };

  const openEditModal = (p) => {
    setEditingProduct(p);
    setFormData({
      name: p.name || '',
      sku: p.sku || '',
      description: p.description || '',
      price: p.price || '',
      cost: p.cost || '',
      stock: p.stock || '0',
      min_stock: p.min_stock || '5',
    });
    setErrors({});
    setProductModalOpen(true);
  };

  const openStockModal = (p) => {
    setSelectedProduct(p);
    setStockForm({ quantity: '', type: 'add', notes: '' });
    setStockModalOpen(true);
  };

  const openKardexModal = async (p) => {
    setSelectedProduct(p);
    setKardexModalOpen(true);
    setKardexLoading(true);
    try {
      const res = await productsService.getMovements(p.id);
      if (res && res.data) {
        setMovements(res.data);
      }
    } catch (err) {
      toastError('Error al cargar historial kardex.');
    } finally {
      setKardexLoading(false);
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});

    try {
      if (editingProduct) {
        await productsService.update(editingProduct.id, formData);
        success('Producto actualizado.');
      } else {
        await productsService.create(formData);
        success('Producto añadido al inventario.');
      }
      setProductModalOpen(false);
      fetchProducts();
    } catch (err) {
      if (err.errors) {
        setErrors(err.errors);
      }
      toastError(err.data?.message || err.message || 'Error al guardar producto.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStockSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await productsService.adjustStock(selectedProduct.id, stockForm);
      success('Stock actualizado y registrado en Kardex.');
      setStockModalOpen(false);
      fetchProducts();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al ajustar stock.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!productToDelete) return;
    setSubmitting(true);
    try {
      await productsService.delete(productToDelete.id);
      success('Producto eliminado del catálogo.');
      setDeleteConfirmOpen(false);
      setProductToDelete(null);
      fetchProducts();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al eliminar producto.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
            <Boxes className="w-6 h-6 text-[#08707A] dark:text-teal-400" />
            Catálogo & Control de Inventario
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Administra existencias, costos, precios y monitorea movimientos de stock
          </p>
        </div>

        <Button onClick={openCreateModal} icon={Plus} className="bg-[#08707A] hover:bg-[#065B63]">
          Nuevo Producto
        </Button>
      </div>

      {/* Filter and Search Card */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 w-full">
            <div className="w-full sm:w-80">
              <Input
                placeholder="Buscar por nombre o SKU..."
                icon={Search}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="py-1.5"
              />
            </div>

            <button
              onClick={() => setFilterLowStock(!filterLowStock)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all border ${
                filterLowStock
                  ? 'bg-rose-50 border-rose-200 text-rose-700 dark:bg-rose-950/60 dark:border-rose-800 dark:text-rose-300 font-bold'
                  : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400'
              }`}
            >
              <Filter className="w-3.5 h-3.5" />
              Solo Stock Crítico / Bajo
            </button>
          </div>
        </CardHeader>

        <CardBody className="p-0">
          {loading ? (
            <Spinner label="Cargando catálogo..." className="py-12" />
          ) : products.length === 0 ? (
            <EmptyState
              icon={Boxes}
              title="No hay productos en inventario"
              description={search ? "No se encontraron productos coincidentes." : "Registra artículos o servicios para usarlos rápidamente en tus facturas."}
              actionLabel="Agregar Producto"
              onAction={openCreateModal}
              actionIcon={Plus}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                  <tr>
                    <th className="py-3 px-4">Producto & SKU</th>
                    <th className="py-3 px-4">Precio Venta</th>
                    <th className="py-3 px-4">Costo Compra</th>
                    <th className="py-3 px-4 text-center">Existencias</th>
                    <th className="py-3 px-4 text-center">Estado Stock</th>
                    <th className="py-3 px-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {products.map((p) => {
                    const isLowStock = Number(p.stock) <= Number(p.min_stock);
                    return (
                      <tr
                        key={p.id}
                        className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors"
                      >
                        <td className="py-3.5 px-4">
                          <div className="font-bold text-slate-900 dark:text-slate-100">
                            {p.name}
                          </div>
                          {p.sku && (
                            <div className="text-[11px] text-slate-400 font-mono">
                              SKU: {p.sku}
                            </div>
                          )}
                        </td>

                        <td className="py-3.5 px-4 font-mono font-bold text-slate-800 dark:text-slate-200">
                          ${Number(p.price || 0).toFixed(2)}
                        </td>

                        <td className="py-3.5 px-4 font-mono text-slate-500">
                          ${Number(p.cost || 0).toFixed(2)}
                        </td>

                        <td className="py-3.5 px-4 text-center">
                          <span className="font-mono font-bold text-slate-900 dark:text-slate-100 text-sm">
                            {p.stock}
                          </span>
                          <span className="text-[10px] text-slate-400 ml-1">unid.</span>
                        </td>

                        <td className="py-3.5 px-4 text-center">
                          {isLowStock ? (
                            <Badge variant="danger" size="sm" dot>Stock Bajo (&le;{p.min_stock})</Badge>
                          ) : (
                            <Badge variant="success" size="sm" dot>Disponible</Badge>
                          )}
                        </td>

                        <td className="py-3.5 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={ArrowUpDown}
                              onClick={() => openStockModal(p)}
                              title="Ajustar stock (+/-)"
                            >
                              Ajustar
                            </Button>

                            <Button
                              variant="ghost"
                              size="sm"
                              icon={History}
                              onClick={() => openKardexModal(p)}
                              title="Ver historial Kardex"
                            />

                            <Button
                              variant="ghost"
                              size="sm"
                              icon={Edit2}
                              onClick={() => openEditModal(p)}
                              title="Editar producto"
                            />

                            <Button
                              variant="ghost"
                              size="sm"
                              icon={Trash2}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
                              onClick={() => {
                                setProductToDelete(p);
                                setDeleteConfirmOpen(true);
                              }}
                              title="Eliminar producto"
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create / Edit Product Modal */}
      <Modal
        isOpen={productModalOpen}
        onClose={() => !submitting && setProductModalOpen(false)}
        title={editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
        subtitle="Configura precios, códigos y umbral de alerta de existencias"
      >
        <form onSubmit={handleProductSubmit} className="p-6 space-y-4">
          <Input
            label="Nombre del Producto o Servicio"
            placeholder="Ej: Licencia Anual de Software"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            error={errors.name?.[0]}
            required
            autoFocus
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Código SKU / Referencia"
              placeholder="PROD-001"
              value={formData.sku}
              onChange={(e) => setFormData(prev => ({ ...prev, sku: e.target.value }))}
              error={errors.sku?.[0]}
            />

            <Input
              label="Precio de Venta"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={formData.price}
              onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
              error={errors.price?.[0]}
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Input
              label="Costo de Compra"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={formData.cost}
              onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value }))}
              error={errors.cost?.[0]}
            />

            <Input
              label="Stock Inicial"
              type="number"
              min="0"
              placeholder="0"
              value={formData.stock}
              onChange={(e) => setFormData(prev => ({ ...prev, stock: e.target.value }))}
              error={errors.stock?.[0]}
              required
              disabled={Boolean(editingProduct)}
            />

            <Input
              label="Stock Mínimo (Alerta)"
              type="number"
              min="0"
              placeholder="5"
              value={formData.min_stock}
              onChange={(e) => setFormData(prev => ({ ...prev, min_stock: e.target.value }))}
              error={errors.min_stock?.[0]}
            />
          </div>

          <Textarea
            label="Descripción Detallada (Opcional)"
            placeholder="Especificaciones o detalles del ítem..."
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            rows={2}
          />

          <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
            <Button
              variant="secondary"
              onClick={() => setProductModalOpen(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={submitting}
              className="bg-[#08707A] hover:bg-[#065B63]"
            >
              {editingProduct ? 'Guardar Cambios' : 'Añadir Producto'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Stock Adjustment Modal */}
      <Modal
        isOpen={stockModalOpen}
        onClose={() => !submitting && setStockModalOpen(false)}
        title={`Ajustar Stock: ${selectedProduct?.name}`}
        subtitle={`Stock actual en almacén: ${selectedProduct?.stock} unidades`}
      >
        <form onSubmit={handleStockSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => setStockForm(prev => ({ ...prev, type: 'add' }))}
              className={`p-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 border transition-all ${
                stockForm.type === 'add'
                  ? 'bg-emerald-50 border-emerald-300 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 font-black ring-2 ring-emerald-500/20'
                  : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-600'
              }`}
            >
              <TrendingUp className="w-4 h-4" />
              Entrada (+)
            </button>

            <button
              type="button"
              onClick={() => setStockForm(prev => ({ ...prev, type: 'subtract' }))}
              className={`p-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 border transition-all ${
                stockForm.type === 'subtract'
                  ? 'bg-rose-50 border-rose-300 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 font-black ring-2 ring-rose-500/20'
                  : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-600'
              }`}
            >
              <TrendingDown className="w-4 h-4" />
              Salida (-)
            </button>

            <button
              type="button"
              onClick={() => setStockForm(prev => ({ ...prev, type: 'set' }))}
              className={`p-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 border transition-all ${
                stockForm.type === 'set'
                  ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 font-black ring-2 ring-blue-500/20'
                  : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-600'
              }`}
            >
              Fijar Total
            </button>
          </div>

          <Input
            label={stockForm.type === 'set' ? 'Nueva Cantidad Total Exacta' : 'Cantidad a Modificar'}
            type="number"
            min="1"
            placeholder="1"
            value={stockForm.quantity}
            onChange={(e) => setStockForm(prev => ({ ...prev, quantity: e.target.value }))}
            required
            autoFocus
          />

          <Input
            label="Motivo del Ajuste (Kardex)"
            placeholder="Ej: Compra de mercancía, merma, devolución..."
            value={stockForm.notes}
            onChange={(e) => setStockForm(prev => ({ ...prev, notes: e.target.value }))}
          />

          <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
            <Button
              variant="secondary"
              onClick={() => setStockModalOpen(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={submitting}
              className="bg-[#08707A] hover:bg-[#065B63]"
            >
              Confirmar Ajuste
            </Button>
          </div>
        </form>
      </Modal>

      {/* Kardex Modal */}
      <Modal
        isOpen={kardexModalOpen}
        onClose={() => setKardexModalOpen(false)}
        title={`Historial de Movimientos Kardex`}
        subtitle={`Producto: ${selectedProduct?.name}`}
        size="lg"
      >
        <div className="p-6">
          {kardexLoading ? (
            <Spinner label="Cargando historial..." className="py-8" />
          ) : movements.length === 0 ? (
            <p className="text-center text-xs text-slate-400 py-8">
              No hay movimientos registrados para este producto.
            </p>
          ) : (
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                  <tr>
                    <th className="py-2.5 px-3">Fecha</th>
                    <th className="py-2.5 px-3">Tipo / Motivo</th>
                    <th className="py-2.5 px-3 text-right">Variación</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {movements.map((m) => {
                    const isPositive = Number(m.quantity) > 0;
                    return (
                      <tr key={m.id}>
                        <td className="py-2.5 px-3 text-slate-500">
                          {new Date(m.created_at).toLocaleString()}
                        </td>
                        <td className="py-2.5 px-3">
                          <span className="font-bold text-slate-800 dark:text-slate-200 uppercase text-[10px]">
                            {m.type}
                          </span>
                          {m.notes && <span className="text-slate-500 ml-1.5">· {m.notes}</span>}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono font-black">
                          <span className={isPositive ? 'text-emerald-600' : 'text-rose-600'}>
                            {isPositive ? `+${m.quantity}` : m.quantity}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="¿Eliminar producto?"
        message={`¿Estás seguro de que deseas eliminar "${productToDelete?.name}"? Si está referenciado en facturas existentes, no se podrá borrar por integridad referencial.`}
        confirmText="Eliminar Producto"
        isLoading={submitting}
      />
    </div>
  );
};
